import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import getFingerprint from "../validations/fingerprint";
import store from "../store";
import { logout } from "../store/userSlice";
import { queryClient } from "../lib/queryClient";

type GetAPI = {
  params: any;
  url: string;
  type: "axios" | "admin" | "buyadmin" | "custom" | "new_admin";
  urlPath?: string;
};
type PostAPI = {
  body: any;
  url: string;
  type: "axios" | "admin" | "buyadmin" | "custom" | "new_admin";
  urlPath?: string;
};

interface AxiosInstanceWithAuth extends AxiosInstance {
  interceptors: {
    request: AxiosInstance["interceptors"]["request"];
    response: AxiosInstance["interceptors"]["response"];
  };
}

export const commonHeaders: AxiosRequestConfig["headers"] = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-internal-app": "Sharepal",
};

const new_axios: AxiosInstanceWithAuth = axios.create({
  headers: commonHeaders,
});

const adminApiCache = new Map<string, AxiosInstanceWithAuth>();
let isHandlingUnauthorized = false;

export const getAdminApi = (url: string, type?: "xano" | "other") => {
  const cacheKey = `${url}::${type ?? "default"}`;
  const cachedApi = adminApiCache.get(cacheKey);
  if (cachedApi) {
    return cachedApi;
  }

  const BASEURL =
    type === "other" || url.startsWith("https://")
      ? url
      : "https://api.sharepal.in/api:" + url;

  const api: AxiosInstanceWithAuth = axios.create({
    baseURL: BASEURL,
    headers: commonHeaders,
  });

  api.interceptors.request.use(
    async (config: any) => {
      const token =
        typeof window !== "undefined" && localStorage.getItem("token");
      if (token) {
        if (!config.headers) {
          config.headers = {};
        }
        try {
          const fingerprint = await getFingerprint();
          config.headers["User-Id"] = fingerprint;
        } catch (error) {
          console.warn("Failed to get fingerprint:", error);
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        if (!isHandlingUnauthorized) {
          isHandlingUnauthorized = true;
          localStorage.removeItem("token");
          localStorage.removeItem("uid");
          store.dispatch(logout());
          queryClient.clear();

          if (
            typeof window !== "undefined" &&
            !window.location.pathname.startsWith("/auth")
          ) {
            window.location.href = "/auth/login";
          }

          setTimeout(() => {
            isHandlingUnauthorized = false;
          }, 0);
        }
      } else {
        console.error("An error occurred:", error.message);
      }
      return Promise.reject(error);
    }
  );

  adminApiCache.set(cacheKey, api);
  return api;
};

export const API = {
  axios: new_axios,
  admin: getAdminApi("1sfs8wHe"),
  new_admin: getAdminApi("3sDks8XW"),
  buyadmin: getAdminApi("NEQR_Y5n"),
};

export const getApi = async <T>({ params, url, type = "axios" }: GetAPI) => {
  const { data } = await API[type].get(url, { params });
  return data as T;
};

export const postApi = async <T>({ body, url, type = "axios" }: PostAPI) => {
  const { data } = await API[type].post(url, body);
  return data as T;
};

export const deleteApi = async <T>({ body, url, type = "axios" }: PostAPI) => {
  const { data } = await API[type].delete(url, {
    data: body,
  });
  https: return data as T;
};

export const customPostApi = async <T>({ body, url, urlPath }: PostAPI) => {
  const { data } = await getAdminApi(urlPath ?? "1sfs8wHe").post(url, body);
  return data as T;
};

export const customGetApi = async <T>({ params, url, urlPath }: GetAPI) => {
  const { data } = await getAdminApi(urlPath ?? "1sfs8wHe").get(url, {
    params,
  });
  return data as T;
};
